import { Group, Pagination, Stack, Table, Text } from '@mantine/core'
import {
  JSXElementConstructor,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { cn } from '../../utils'
import FilterWorker from './filter.worker.js?worker'

/**
 * SortableTable
 *
 * A table component that allows sorting, filtering and pagination using web worker for filtering
 *
 * @props:
 * - data: any[] - the data to display
 * - columns: { label: string, render?: (val: any, row: any) => any, key: string, sortable?: boolean }[] - the columns to display
 * - pageSize: number - the number of rows per page
 * - highlightOnHover: boolean - whether to highlight rows on hover
 * - defaultSortBy: string - the default column to sort by
 * - defaultSortAsc: boolean - the default column to sort order, default false
 * - noResultsMessage: string - the message to display when no results are found
 * - highlightSelected: boolean - whether to highlight the selected row
 * - selected: any - the selected row
 * - rowClassname: string - the classname to apply to each row
 * - noLoading: boolean - whether to display a loading message
 * - loadingMessage: string - the message to display when loading
 * - filterBy: string - the filter string
 * - sortBy: string - the column to sort by
 * - sortAsc: boolean - the sort order
 * - filterCallback: (data: any) => boolean - the callback to filter the data
 *
 * @usage:
 * ```tsx
 *  <SortableTable data={data} columns={columns} pageSize={30} highlightOnHover defaultSortBy={'name'} />
 * ```
 */

interface Column<T> {
  label: string | ReactNode
  key?: keyof T
  render?: (val: any, row: T) => ReactNode
  sortable?: boolean
  className?: string
  visibleFrom?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  hiddenFrom?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
}

interface WorkerInputMessage<T> {
  data: T[]
  sortBy: keyof T | ''
  filterBy?: string
  sortAsc?: boolean
  requestId: number
}

interface WorkerOutputPayload<T> {
  result: T[]
  requestId: number
}

type StaleCacheEntry = { signature: string; rows: unknown[] }

const sortableTableStaleCache = new Map<string, StaleCacheEntry>()

function buildSortableStaleSignature<T>(
  data: T[] | undefined,
  filterBy: string | undefined,
  sortBy: keyof T | '',
  sortAsc: boolean,
  staleDataSignature?: string
): string {
  if (staleDataSignature !== undefined && staleDataSignature !== '') {
    return staleDataSignature
  }
  return [
    String(data?.length ?? 0),
    String(filterBy ?? ''),
    String(sortBy ?? ''),
    String(sortAsc)
  ].join('\0')
}

export interface SortableTableProps<T> {
  columns: Column<T>[]
  data?: T[]
  defaultSortBy?: keyof T
  defaultSortAsc?: boolean
  filterBy?: string
  highlightOnHover?: boolean
  highlightSelected?: boolean
  loadingMessage?: string
  loading?: boolean
  noLoading?: boolean
  noLoadingWhileData?: boolean
  noResultsMessage?: string | ReactNode
  pageSize?: number
  selected?: T
  filterCallback?: (data: T) => boolean
  noHeader?: boolean
  rowOnClick?: (row: T) => void
  paginationWrapper?: JSXElementConstructor<any>
  tableWrapper?: JSXElementConstructor<any>
  loadingComponent?: ReactNode | string
  /** When set (e.g. route or entity id), last filtered/sorted rows are cached for instant remounts. Pair with `staleDataSignature` when row count alone is not enough to detect new data. */
  staleCacheKey?: string
  /** Bust or validate module cache; defaults to length + filter + sort state when omitted. */
  staleDataSignature?: string
  classNames?: {
    tableWrapper?: string
    table?: string
    row?: string
    header?: string
  }
  size?: 'sm' | 'md' | 'lg' | 'xl'
}

export interface ColumnTitleProps<T> {
  title: string | ReactNode
  sortKey?: keyof T
  sortAsc: boolean
  sortBy: keyof T | ''
  setSortAsc: (asc: boolean) => void
  defaultSortAsc: boolean
  setSortBy: (key: keyof T | '') => void
  activeClass?: string
  className?: string
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
}

export const SortableTable = <T,>({
  columns,
  data,
  loading = false,
  defaultSortBy,
  defaultSortAsc = true,
  filterBy,
  highlightSelected = true,
  highlightOnHover = false,
  loadingMessage = 'Loading data...',
  noResultsMessage = 'No results found.',
  loadingComponent = <Text>Loading...</Text>,
  noLoading = false,
  noLoadingWhileData = false,
  pageSize = 30,
  selected,
  noHeader,
  rowOnClick,
  paginationWrapper,
  tableWrapper,
  classNames = {},
  size = 'sm',
  staleCacheKey,
  staleDataSignature
}: SortableTableProps<T>) => {
  const [sortBy, setSortBy] = useState<keyof T | ''>(defaultSortBy || '')
  const [sortAsc, setSortAsc] = useState<boolean>(defaultSortAsc || false)
  const [page, setPage] = useState<number>(1)
  const [filteredList, setFilteredList] = useState<T[]>(() => {
    const sig = buildSortableStaleSignature(
      data,
      filterBy,
      (defaultSortBy || '') as keyof T | '',
      defaultSortAsc || false,
      staleDataSignature
    )
    if (staleCacheKey) {
      const hit = sortableTableStaleCache.get(staleCacheKey)
      if (hit?.signature === sig) return hit.rows as T[]
    }
    return []
  })
  const [paginatedList, setPaginatedList] = useState<T[]>([])
  const filterWorkerRef = useRef<Worker | null>(null)
  const requestIdRef = useRef(0)
  const stalePropsRef = useRef({
    data,
    filterBy,
    sortBy: (defaultSortBy || '') as keyof T | '',
    sortAsc: defaultSortAsc || false,
    staleCacheKey,
    staleDataSignature
  })

  stalePropsRef.current = {
    data,
    filterBy,
    sortBy,
    sortAsc,
    staleCacheKey,
    staleDataSignature
  }

  useEffect(() => {
    const worker = new FilterWorker()
    filterWorkerRef.current = worker
    worker.onmessage = (e: MessageEvent<WorkerOutputPayload<T>>) => {
      const { result, requestId } = e.data
      if (requestId !== requestIdRef.current) return
      setFilteredList(result ?? [])
      const p = stalePropsRef.current
      if (p.staleCacheKey) {
        const sig = buildSortableStaleSignature(
          p.data,
          p.filterBy,
          p.sortBy,
          p.sortAsc,
          p.staleDataSignature
        )
        sortableTableStaleCache.set(p.staleCacheKey, {
          signature: sig,
          rows: result ?? []
        })
      }
    }

    return () => {
      worker.terminate()
      filterWorkerRef.current = null
    }
  }, [])

  useEffect(() => {
    const worker = filterWorkerRef.current
    if (!data?.length) {
      setFilteredList([])
      return
    }
    if (!worker) {
      return
    }
    const message: WorkerInputMessage<T> = {
      data,
      sortBy,
      filterBy,
      sortAsc,
      requestId: ++requestIdRef.current
    }
    worker.postMessage(message)
  }, [data, filterBy, sortAsc, sortBy])

  useEffect(() => {
    if (!filteredList) return

    const _paginatedList = filteredList.slice(
      (page - 1) * pageSize,
      (page - 1) * pageSize + pageSize
    ) as []

    if (filteredList.length > 0) {
      const newPage = Math.ceil(filteredList.length / pageSize)
      if (page > newPage) {
        setPage(newPage)
      }
    }

    setPaginatedList(_paginatedList)
  }, [filteredList, page, pageSize])

  if (!columns) {
    return <h1>No Columns specified</h1>
  }

  const columnTitle = useMemo(
    () =>
      (
        title: string | ReactNode,
        key?: keyof T,
        className?: string,
        index?: number
      ) => (
        <ColumnTitle
          key={`k_${index}_${title}`}
          title={title}
          sortKey={key}
          sortAsc={sortAsc}
          sortBy={sortBy}
          defaultSortAsc={defaultSortAsc || false}
          setSortAsc={setSortAsc}
          setSortBy={setSortBy}
          className={className}
          size={size}
        />
      ),
    [sortAsc, sortBy, defaultSortAsc, size]
  )

  const TableHeader = useMemo(() => {
    return (
      <Table.Thead className={cn('ui-sortableTable-header', classNames.header)}>
        <Table.Tr role="row">
          {columns.map((col, i) =>
            columnTitle(
              col.label,
              col.sortable || col.sortable === undefined ? col.key : undefined,
              col.className,
              i
            )
          )}
        </Table.Tr>
      </Table.Thead>
    )
  }, [columns, columnTitle, classNames.header])

  const TableBody = useMemo(() => {
    return (
      <Table.Tbody>
        {paginatedList.map((r, si: number) => {
          return (
            <Table.Tr
              role="row"
              key={'rsi' + si}
              className={`ui-sortableTable-body hover:ui-sortableTable-hover ${selected === r ? 'active ' + highlightSelected + ' bg-surface' : ''} ${classNames.row} ${rowOnClick ? 'cursor-pointer' : ''}`}
              onClick={() => rowOnClick && rowOnClick(r)}
            >
              {columns.map((col, ci: number) => (
                <Table.Td key={'ci' + ci} role="cell" className={col.className}>
                  {col.render ? (
                    col.render(r[col.key as keyof typeof r] || null, r)
                  ) : (
                    <>{r[col.key as keyof typeof r]}</>
                  )}
                </Table.Td>
              ))}
            </Table.Tr>
          )
        })}
      </Table.Tbody>
    )
  }, [
    columns,
    highlightSelected,
    paginatedList,
    selected,
    classNames.row,
    rowOnClick
  ])

  const PaginationBox = useMemo(() => {
    return (
      data &&
      Math.ceil(data.length / pageSize) > 1 && (
        <Pagination
          classNames={{ root: 'ui-sc-pagination' }}
          total={Math.ceil(filteredList.length / pageSize)}
          value={page}
          onChange={setPage}
        />
      )
    )
  }, [data, page, filteredList.length])

  if (!loading && filteredList.length === 0 && noResultsMessage) {
    return <>{noResultsMessage}</>
  }

  return (
    <Stack>
      <Stack component={tableWrapper}>
        <Table
          className={cn('ui-sc-sortableTable', classNames.table)}
          highlightOnHover={highlightOnHover}
          role="table"
        >
          {!noHeader && TableHeader}
          {TableBody}
        </Table>

        {loading &&
          !data &&
          !noLoading &&
          !noLoadingWhileData &&
          (loadingComponent || loadingMessage)}
      </Stack>

      {PaginationBox && (
        <Group component={paginationWrapper}>{PaginationBox}</Group>
      )}
    </Stack>
  )
}

export const ColumnTitle = <T,>({
  title = 'label',
  sortBy,
  setSortBy = () => {},
  setSortAsc = () => {},
  activeClass = '',
  defaultSortAsc = true,
  sortAsc = false,
  sortKey,
  className = '',
  size = 'sm'
}: ColumnTitleProps<T>) => (
  <Table.Th
    key={'th' + title}
    aria-sort={
      sortKey === sortBy ? (sortAsc ? 'ascending' : 'descending') : 'none'
    }
    className={cn(
      `ui-sortableTable-columnTitle`,
      size && `text-${size}`,
      className,
      sortKey &&
        sortBy === sortKey &&
        'cursor-pointer border-b-2 border-white ' + activeClass
    )}
    tabIndex={0}
    style={{ cursor: sortKey ? 'pointer' : 'default' }}
    onClick={() => {
      if (!sortKey) return
      if (sortKey === sortBy) {
        setSortAsc(!sortAsc)
      } else {
        setSortBy(sortKey)
        setSortAsc(defaultSortAsc)
      }
    }}
  >
    {title}
  </Table.Th>
)
